import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/axios";
import { LoadingScreen } from "../components/common/LoadingScreen";
import { formatRevenueFee } from "../constants/pricing";
import type { AvailablePlan } from "../services/permissionsService";

const Pricing = () => {
  // Fetch available plans from public endpoint
  const { data: backendPlans, isLoading: isLoadingPlans } = useQuery<AvailablePlan[]>({
    queryKey: ["public-plans"],
    queryFn: async () => {
      const response = await api.get("/permissions/public/plans");
      return response.data;
    },
  });

  // Convert backend plans to display format
  const plans = backendPlans?.map((plan, index) => {
    // Use plan descriptions - Order 0 is plan description AND in features list, Order 1+ are also features
    const allDescriptions = plan.descriptions || [];
    const planDescription = allDescriptions.find(d => d.order === 0)?.description || '';
    const featureDescriptions = allDescriptions
      .filter(d => d.order >= 0) // Include order 0 in features list
      .sort((a, b) => a.order - b.order)
      .map(desc => desc.description);

    // Included AI search quota comes from the backend plan config
    if (plan.aiSearchQuestions) {
      featureDescriptions.push(`${plan.aiSearchQuestions} Social searches per month`);
    }

    return {
      id: plan.id,
      name: plan.name,
      price: plan.price.toString(),
      revenue: formatRevenueFee(plan.commissionRate),
      description: planDescription,
      features: featureDescriptions,
      isPopular: index === backendPlans.length - 1, // Mark last plan as popular
    };
  }) || [];

  if (isLoadingPlans) {
    return <LoadingScreen />;
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Simple, Transparent Pricing</h1>
        <p className="text-xl text-muted-foreground">Choose the plan that best fits your business needs</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="relative bg-card rounded-lg border-2 p-8 text-center flex flex-col"
          >
            {plan.isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-600 text-white text-sm rounded-full whitespace-nowrap font-semibold">
                Most Popular
              </div>
            )}
            
            <div className="mb-4">
              <div className="text-4xl font-bold mb-2">
                ${plan.price}
                <span className="text-base font-normal text-muted-foreground">/mo</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
              
              <p className="text-brand-600 font-medium">{plan.revenue}</p>
            </div>

            {/* Features List */}
            {plan.features.length > 0 && (
              <ul className="space-y-2 mb-6 flex-grow text-left">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <svg
                      className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            )}

            {/* CTA Button */}
            <a
              href={
                import.meta.env.VITE_SHOPIFY_APP_STORE_URL ||
                "https://apps.shopify.com/reveshare"
              }
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-auto inline-block w-full px-6 py-3 rounded-lg transition-colors font-semibold ${
                plan.isPopular
                  ? "bg-brand-600 text-white hover:bg-brand-700"
                  : "bg-muted text-foreground hover:bg-accent border border-border"
              }`}
            >
              Get Started
            </a>
          </div>
        ))}
      </div>

      <div className="text-center mt-8 text-muted-foreground">
        Transaction fee only applies to orders with a Reveshare generated discount code.
      </div>

      <div className="text-center mt-16">
        <h2 className="text-3xl font-bold mb-4">Start your affiliate program today</h2>
        <p className="text-xl text-muted-foreground mb-8">Grow your Shopify store without an upfront cost.</p>
        
        <a
          href={
            import.meta.env.VITE_SHOPIFY_APP_STORE_URL ||
            "https://apps.shopify.com/reveshare"
          }
          target="_blank"
          rel="noopener noreferrer" 
          className="inline-block px-8 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors font-semibold"
        >
          Install For Free
        </a>
      </div>
    </div>
  );
};

export default Pricing;
